import {
  CheckCircleIcon,
  XMarkIcon,
  MinusCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { ReactComponent as PendingIcon } from '../../icons/svg/pending.svg';
import { CSSProperties, useCallback } from 'react';
import { COMMON_TRANSLATE_KEYS } from '../../constants/translate-keys';
import { useTranslation } from 'react-i18next';

interface TProps {
  status: string;
  count?: number;
  className?: string;
  style?: CSSProperties;
  dontShrink?: boolean;
  confirmedStatusKey?: string;
  isExpired?: boolean;
  noXMargin?: boolean;
  textClassName?: string;
}

const EventStatusBadge = ({
  status,
  className,
  style,
  count = 1,
  dontShrink,
  confirmedStatusKey,
  textClassName,
  isExpired = false,
  noXMargin = false,
}: TProps) => {
  const { t } = useTranslation();

  const renderStatusBadge = useCallback(() => {
    let statusColor: string = 'gray';
    let borderColor: string = 'gray';
    let statusKey: string = status;
    let IconComponent = null;
    let responsiveDisplay = 'hidden xl:flex';

    switch (status) {
      case 'CONFIRMED':
        statusColor = 'text-functionalRed';
        borderColor = 'border-functionalRed';
        IconComponent = MinusCircleIcon;
        statusKey = confirmedStatusKey || COMMON_TRANSLATE_KEYS.TAKEN;
        break;
      case 'DECLINED':
        statusColor = 'text-functionalRed';
        borderColor = 'border-functionalRed';
        IconComponent = XMarkIcon;
        statusKey = COMMON_TRANSLATE_KEYS.DECLINED_BY_OWNER;
        break;
      case 'PENDING':
        statusColor = isExpired ? 'text-additionalGray' : 'text-mainBlue';
        borderColor = isExpired ? 'border-additionalGray' : 'border-mainBlue';
        IconComponent = isExpired ? ClockIcon : PendingIcon;
        statusKey = isExpired
          ? COMMON_TRANSLATE_KEYS.NULLED
          : COMMON_TRANSLATE_KEYS.PENDING;
        break;
      case 'CANCELED':
        statusColor = 'text-functionalRed';
        borderColor = 'border-functionalRed';
        IconComponent = XMarkIcon;
        statusKey = COMMON_TRANSLATE_KEYS.CANCELED_BY_PLAYER;
        break;
      case 'FREE':
        statusColor = 'text-mainGreen';
        borderColor = 'border-mainGreen';
        IconComponent = CheckCircleIcon;
        statusKey = COMMON_TRANSLATE_KEYS.FREE;
        break;
      default:
        statusKey = COMMON_TRANSLATE_KEYS.UNKNOWN;
    }

    if (dontShrink) {
      responsiveDisplay = 'flex';
    }

    return (
      <div
        className={`flex items-center ${
          noXMargin ? 'my-4' : 'ml-6 m-4'
        } mt-4 py-1 px-3 border justify-center gap-2 rounded-xl ${borderColor} ${className}`}
        style={style}
      >
        {IconComponent && (
          <IconComponent className={`h-5 w-[25px] ${statusColor}`} />
        )}
        <span
          className={`${statusColor} flex-shrink-0 ${responsiveDisplay} ${textClassName}`}
        >
          {t(statusKey, { count })}
        </span>
      </div>
    );
  }, [
    status,
    dontShrink,
    noXMargin,
    className,
    style,
    t,
    count,
    confirmedStatusKey,
    isExpired,
    textClassName,
  ]);

  return renderStatusBadge();
};

export default EventStatusBadge;
